import { useState, useEffect, useCallback } from "react";
import { socialProofData } from "../data/mock";
import { Quote, ChevronLeft, ChevronRight, MapPin, User } from "lucide-react";

export const SocialProofSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const total = socialProofData.testimonials.length;

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % total);
  }, [total]);

  const prev = () => {
    setActiveIndex((prev) => (prev - 1 + total) % total);
  };

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const current = socialProofData.testimonials[activeIndex];

  return (
    <section
      id="testimonials"
      className="relative py-28"
      style={{ background: "#1a1c1b" }}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />

      <div className="max-w-[87.5rem] mx-auto px-10">
        {/* Section Header */}
        <div className="mb-16">
          <span
            className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5"
            style={{ fontFamily: "'Inter', Arial, sans-serif" }}
          >
            Social Proof
          </span>
          <h2
            className="font-black uppercase leading-[0.88]"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              fontSize: "clamp(1.8rem, 1.4rem + 2vw, 3rem)",
              letterSpacing: "-0.02em",
            }}
          >
            <span className="text-[#dfddd6]">What </span>
            <span className="text-[#d9fb06]">Clients Say</span>
          </h2>
        </div>

        {/* Testimonial Card */}
        <div className="relative max-w-4xl">
          <div className="bg-[#302f2c]/50 border border-[#3f4816]/20 p-10 md:p-14 rounded-sm min-h-[260px] flex flex-col justify-between">
            <div>
              <Quote size={30} className="text-[#3f4816]/50 mb-5" />
              <p
                className="text-[#dfddd6] text-lg md:text-xl leading-relaxed font-medium"
                style={{ fontFamily: "'Inter', Arial, sans-serif" }}
              >
                "{current.quote}"
              </p>
            </div>

            <div className="mt-8 flex items-center gap-4">
              {/* Avatar placeholder */}
              <div className="w-10 h-10 rounded-full bg-[#3f4816]/20 border border-[#3f4816]/30 flex items-center justify-center">
                <User size={16} className="text-[#d9fb06]/60" />
              </div>
              <div>
                <p
                  className="text-[#dfddd6] text-sm font-semibold"
                  style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                >
                  {current.name}
                </p>
                <div className="flex items-center gap-1.5">
                  <MapPin size={12} className="text-[#3f4816]" />
                  <span
                    className="text-[#888680] text-xs"
                    style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                  >
                    {current.location}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-11 h-11 border border-[#3f4816]/30 rounded-full flex items-center justify-center text-[#888680] transition-all duration-300 hover:border-[#3f4816]/60 hover:text-[#dfddd6]"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              className="w-11 h-11 border border-[#3f4816]/30 rounded-full flex items-center justify-center text-[#888680] transition-all duration-300 hover:border-[#3f4816]/60 hover:text-[#dfddd6]"
            >
              <ChevronRight size={18} />
            </button>

            {/* Dots */}
            <div className="flex gap-2 ml-4">
              {socialProofData.testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? "bg-[#d9fb06] w-6"
                      : "bg-[#3f4816] w-2 hover:bg-[#888680]"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
